import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Refresh,
  Close,
  Save,
  Person,
  Email,
  AccountCircle,
  Lock,
  VerifiedUser,
  MoreVert,
  Visibility,
  VisibilityOff,
  PersonAdd
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { blue, green, red, grey, orange } from '@mui/material/colors';
import pb from './pocketbase';
import { useNavigate } from 'react-router-dom';
// Styled components
const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const StatusChip = styled(Chip)(({ verified, theme }) => ({
  fontWeight: 600,
  ...(verified && {
    backgroundColor: green[100],
    color: green[800],
  }),
  ...(!verified && {
    backgroundColor: orange[100],
    color: orange[800],
  }),
}));

const AdminManagement = () => {
  // Current date and user
  const currentDateTime = "2025-04-03 05:50:09";
  const currentUser = "MdAbuNafisNiloy";
    const navigate = useNavigate(); 
  // State management
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    emailVisibility: true,
    verified: false
  });
  
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch admins from PocketBase
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      
      // Create filter based on search query
      let filter = '';
      if (searchQuery) {
        filter = `name~"${searchQuery}" || email~"${searchQuery}" || username~"${searchQuery}"`;
      }
      
      // Fetch admins with pagination
      const result = await pb.collection('admin').getList(page + 1, rowsPerPage, {
        filter,
        sort: '-created'
      });
      
      setAdmins(result.items);
      setTotalAdmins(result.totalItems);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching admins:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load administrators',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Load data when component mounts or dependencies change
  useEffect(() => {
    fetchAdmins();
  }, [page, rowsPerPage, searchQuery]);

  // Handle form change
  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for the field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    
    if (dialogMode === 'add' && !formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (dialogMode === 'add' && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setIsSubmitting(true);
      
      // Check if we're updating our own account
      const isUpdatingSelf = dialogMode === 'edit' && 
                           selectedAdmin.id === pb.authStore.model?.id;
  
      if (dialogMode === 'add') {
        // Create new admin
        await pb.collection('admin').create({
          name: formData.name.trim(),
          email: formData.email.trim(),
          username: formData.username.trim(),
          password: formData.password,
          passwordConfirm: formData.password,
          emailVisibility: formData.emailVisibility,
          verified: formData.verified
        });
        
        setSnackbar({
          open: true,
          message: 'Administrator created successfully',
          severity: 'success'
        });
      } else {
        // Update existing admin
        const data = {
          name: formData.name.trim(),
          email: formData.email.trim(),
          username: formData.username.trim(),
          emailVisibility: formData.emailVisibility,
          verified: formData.verified
        };
        
        // Only include password if it was provided
        if (formData.password) {
          data.password = formData.password;
          data.passwordConfirm = formData.password;
        }
        
        await pb.collection('admin').update(selectedAdmin.id, data);
        
        setSnackbar({
          open: true,
          message: 'Administrator updated successfully',
          severity: 'success'
        });
  
        // If updating own account, force logout
        if (isUpdatingSelf) {
          setSnackbar({
            open: true,
            message: 'Account updated. Please login again with your new credentials.',
            severity: 'info'
          });
          pb.authStore.clear();
          navigate('/login'); // Redirect to login page
          return; // Exit early since we're logging out
        }
      }
      
      // Refresh data and close dialog
      fetchAdmins();
      setDialogOpen(false);
    } catch (error) {
      console.error('Error saving admin:', error);
      
      let errorMsg = 'Failed to save administrator';
      const fieldErrors = {};
      
      // Handle PocketBase validation errors
      if (error.data?.data) {
        Object.entries(error.data.data).forEach(([field, messages]) => {
          fieldErrors[field] = Array.isArray(messages) ? messages[0] : messages;
        });
        
        // Use the first error message for the snackbar
        const firstErrorField = Object.keys(error.data.data)[0];
        const firstErrorMsg = error.data.data[firstErrorField];
        
        if (firstErrorMsg) {
          errorMsg = `${firstErrorField}: ${Array.isArray(firstErrorMsg) ? firstErrorMsg[0] : firstErrorMsg}`;
        }
      }
      // Handle other error messages
      else if (error.message) {
        errorMsg = error.message;
      }
      
      setErrors(fieldErrors);
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open dialog to add a new admin
  const handleAddAdmin = () => {
    setFormData({
      name: '',
      email: '',
      username: '',
      password: '',
      emailVisibility: true,
      verified: false
    });
    setDialogMode('add');
    setErrors({});
    setShowPassword(false);
    setDialogOpen(true);
  };

  // Open dialog to edit an existing admin
  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name || '',
      email: admin.email || '',
      username: admin.username || '',
      password: '',
      emailVisibility: admin.emailVisibility,
      verified: admin.verified
    });
    setDialogMode('edit');
    setErrors({});
    setShowPassword(false);
    setDialogOpen(true);
  };

  // Open dialog to view admin details
  const handleViewAdmin = (admin) => {
    setSelectedAdmin(admin);
    setViewDialog(true);
  };

  // Open confirmation dialog to delete an admin
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmDeleteDialog(true);
  };

  // Delete an admin
  const handleDeleteConfirm = async () => {
    try {
      setIsSubmitting(true);
      
      await pb.collection('admin').delete(deleteId);
      
      setSnackbar({
        open: true,
        message: 'Administrator deleted successfully',
        severity: 'success'
      });
      
      fetchAdmins();
      setConfirmDeleteDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error deleting admin:', error);
      
      let errorMsg = 'Failed to delete administrator';
      
      if (error.message.includes('The currently logged admin')) {
        errorMsg = 'You cannot delete your own account';
      }
      
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
      
      setIsSubmitting(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get an avatar color based on name
  const getAvatarColor = (name) => {
    const colors = [blue[500], green[500], orange[500], red[500], grey[700]];
    let sum = 0;
    
    if (!name) return colors[0];
    
    for (let i = 0; i < name.length; i++) {
      sum += name.charCodeAt(i);
    }
    
    return colors[sum % colors.length];
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Administrators
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage administrator accounts and permissions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={handleAddAdmin}
          sx={{ borderRadius: 2 }}
        >
          Add Administrator
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or username..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  setSearchQuery('');
                  fetchAdmins();
                }}
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Admins Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Administrator</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from(new Array(5)).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ))
              ) : admins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Person sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        No Administrators Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {searchQuery ? 'Try adjusting your search.' : 'Create your first administrator to get started.'}
                      </Typography>
                      {!searchQuery && (
                        <Button 
                          variant="contained" 
                          startIcon={<PersonAdd />}
                          onClick={handleAddAdmin}
                        >
                          Add Administrator
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                admins.map((admin) => (
                  <TableRow key={admin.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {admin.username === currentUser ? (
                          <StyledBadge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            variant="dot"
                          >
                            <Avatar 
                              sx={{ 
                                bgcolor: getAvatarColor(admin.name),
                                width: 40,
                                height: 40
                              }}
                            >
                              {admin.name ? admin.name.charAt(0).toUpperCase() : 'A'}
                            </Avatar>
                          </StyledBadge>
                        ) : (
                          <Avatar 
                            sx={{ 
                              bgcolor: getAvatarColor(admin.name),
                              width: 40,
                              height: 40
                            }}
                          >
                            {admin.name ? admin.name.charAt(0).toUpperCase() : 'A'}
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {admin.name || 'Unnamed'}
                          </Typography>
                          {admin.username === currentUser && (
                            <Typography variant="caption" color="primary" fontWeight={600}>
                              Current User
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {admin.username}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {admin.emailVisibility ? admin.email : '(Hidden)'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        size="small"
                        verified={admin.verified}
                        label={admin.verified ? 'Verified' : 'Unverified'}
                        icon={admin.verified ? <VerifiedUser fontSize="small" /> : null}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(admin.created)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton
                            color="primary"
                            onClick={() => handleViewAdmin(admin)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            color="secondary"
                            onClick={() => handleEditAdmin(admin)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(admin.id)}
                            disabled={admin.username === currentUser}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalAdmins}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add/Edit Admin Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => !isSubmitting && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {dialogMode === 'add' ? 'Add New Administrator' : 'Edit Administrator'}
            </Typography>
            <IconButton
              onClick={() => setDialogOpen(false)}
              disabled={isSubmitting}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
                error={!!errors.name}
                helperText={errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircle />
                    </InputAdornment>
                  ),
                }}
                disabled={isSubmitting}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleFormChange}
                required
                error={!!errors.username}
                helperText={errors.username}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
                disabled={isSubmitting}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
                required
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
                disabled={isSubmitting}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={dialogMode === 'add' ? 'Password' : 'New Password (leave blank to keep current)'}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleFormChange}
                required={dialogMode === 'add'}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={isSubmitting}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.emailVisibility}
                    onChange={handleFormChange}
                    name="emailVisibility"
                    disabled={isSubmitting}
                  />
                }
                label="Make email visible to other admins"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.verified}
                    onChange={handleFormChange}
                    name="verified"
                    disabled={isSubmitting}
                  />
                }
                label="Mark account as verified"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Admin Dialog */}
      <Dialog 
        open={viewDialog} 
        onClose={() => setViewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedAdmin && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Administrator Details
                </Typography>
                <IconButton onClick={() => setViewDialog(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: getAvatarColor(selectedAdmin.name),
                    width: 64,
                    height: 64
                  }}
                >
                  {selectedAdmin.name ? selectedAdmin.name.charAt(0).toUpperCase() : 'A'}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {selectedAdmin.name || 'Unnamed'}
                  </Typography>
                  <StatusChip
                    size="small"
                    verified={selectedAdmin.verified}
                    label={selectedAdmin.verified ? 'Verified' : 'Unverified'}
                    icon={selectedAdmin.verified ? <VerifiedUser fontSize="small" /> : null}
                  />
                </Box>
              </Box>
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Username
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedAdmin.username}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedAdmin.emailVisibility ? selectedAdmin.email : '(Hidden)'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Created
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDate(selectedAdmin.created)}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last Updated
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDate(selectedAdmin.updated)}
                  </Typography>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={() => setViewDialog(false)}
              >
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => {
                  setViewDialog(false);
                  handleEditAdmin(selectedAdmin);
                }}
              >
                Edit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteDialog}
        onClose={() => !isSubmitting && setConfirmDeleteDialog(false)}
      >
        <DialogTitle>
          Delete Administrator
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete this administrator? This action cannot be undone.
          </Alert>
          <Typography variant="body2">
            Deleting an administrator will remove their access to the system permanently.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteDialog(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Delete />}
          >
            {isSubmitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminManagement;